<template>
  <div class="white-bg p3 m3">
    <div class="flex justify-between items-center">
      <div>
        <p class="h5n m0 bold">{{ getReferencelabel('EMERGENCY_CONTACTS_FORM_TITLE') }}</p>
      </div>
    </div>

    <div v-if="editableEmergencyContacts.length > 0">
      <div class="pt3" v-for="(contact, index) in editableEmergencyContacts" :key="index">
        <hr class="m0 dark-grey" />
        <EmergencyContactForm
          class="pt3"
          :index="index"
          v-model="editableEmergencyContacts[index]"
          @save-contact="saveContact(index)"
          @reset-contact="resetContact(index)"
          @show-delete-contact-modal="deleteContact(index)"
        />
      </div>
    </div>

    <div v-if="!isEmergencyContactLimitReached" class="pt3 col-12">
      <hr class="dark-grey m0" />
      <div class="flex flex-column center items-center pt3 gap2">
        <p class="h5n semi-bold m0 center">{{ getReferencelabel('ADD_EMERGENCY_CONTACT') }}</p>
        <button @click="addEmergencyContact" class="flex items-center justify-center pointer white-bg border dark-blue-border" style="border-width: 3px; width: 40px; height: 40px; border-radius: 50%">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="dark-blue">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor" />
          </svg>
        </button>
      </div>
    </div>
    <Modal v-if="showModal" @close="cancelDelete">
      <template #body>
        <div class="col-12 flex flex-column items-center justify-center white-bg p3">
          <span class="h4n bold mb3">{{ getReferencelabel('DELETE_EMERGENCY_CONTACT') }}</span>
          <div class="flex col-12 gap1">
            <ButtonV2 :reference="'NO'" rounded class="px2 py1 col-6" color="light" :action="cancelDelete" />
            <ButtonV2 :reference="'YES'" rounded class="px2 py1 col-6" color="dark" :action="confirmDelete" />
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';

import { getReferencelabel, isInImpersonateModeNotification, errorNotification, isObjectEmpty } from '@/mixins/utils';
import { updateUser } from '@/mixins/requests';
import ButtonV2 from '@/components/elements/ButtonV2.vue';
import Modal from '@/components/blocks/Modal';
import EmergencyContactForm from '@/components/forms/EmergencyContactForm';

export default {
  name: 'EmergencyContacts',
  components: {
    EmergencyContactForm,
    ButtonV2,
    Modal,
  },
  setup() {
    const store = useStore();

    const editableEmergencyContacts = ref(JSON.parse(JSON.stringify(store.state.user.emergency_contacts)) || []);

    const emergencyContacts = computed(() => store.state.user.emergency_contacts || []);

    const showModal = ref(false);

    const contactToDelete = ref(null);

    const emergencyContactLimit = 1;
    const isEmergencyContactLimitReached = computed(() => editableEmergencyContacts.value.length >= emergencyContactLimit);

    function addEmergencyContact() {
      editableEmergencyContacts.value.push({
        firstname: '',
        lastname: '',
        email: '',
        telephone: '',
      });
    }

    function deleteContact(index) {
      contactToDelete.value = index;
      showModal.value = true;
    }

    function cancelDelete() {
      contactToDelete.value = null;
      showModal.value = false;
    }

    function confirmDelete() {
      editableEmergencyContacts.value.splice(contactToDelete.value, 1);
      const updatedEmergencyContacts = emergencyContacts.value;
      updatedEmergencyContacts.splice(contactToDelete.value, 1)
      updateUserData(updatedEmergencyContacts);
      showModal.value = false;
    }

    function resetContact(index) {
      if (emergencyContacts.value[index]) {
        Object.assign(editableEmergencyContacts.value[index], JSON.parse(JSON.stringify(emergencyContacts.value[index])));
      } else {
        editableEmergencyContacts.value.splice(index, 1);
      }
    }

    async function saveContact(index) {
      const updatedEmergencyContacts = emergencyContacts.value;
      updatedEmergencyContacts[index] = editableEmergencyContacts.value[index]; 
      return updateUserData(updatedEmergencyContacts);
    }

    async function updateUserData(emergencyContacts) {
      if (isInImpersonateModeNotification()) {
        return;
      }

      try {
        store.commit('setLoading', true);

        const user = await updateUser({
          ...store.state.user,
          emergency_contacts: emergencyContacts,
        });

        if (!user?.success) {
          throw new Error(user?.message);
        }

        store.commit('setLoading', false);
      } catch (error) {
        console.error('Error updating emergency contacts:', error);
        store.commit('setLoading', false);
        errorNotification('UPDATE_USER_ERROR_NOTIFICATION');
      }
    }
    
    return {
      editableEmergencyContacts,
      showModal,
      getReferencelabel,
      addEmergencyContact,
      deleteContact,
      cancelDelete,
      confirmDelete,
      resetContact,
      isEmergencyContactLimitReached,
      saveContact
    };
  },
};
</script>
